import { useMemo } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import {
  type ReservationDateRow,
  resolveTurnoverWindow,
} from "@/lib/turnoverWindow";
import type { ScheduleTurnoverSelection } from "./ScheduleTurnoverDetail";

function firstName(name: string | null | undefined): string {
  const t = (name || "").trim();
  if (!t) return "Guest";
  return t.split(/\s+/)[0]!;
}

function initialsOf(name: string | null | undefined): string {
  const t = (name || "").trim();
  if (!t) return "?";
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

function shortAssignee(label: string): string {
  const t = label.trim();
  if (!t) return "Unassigned";
  // "Maria R. (Cleaner)" → "Maria R."
  return t.replace(/\s*\([^)]*\)\s*$/, "").trim() || t;
}

type StayBar = {
  reservationId: string;
  guestName: string | null;
  guestPhotoUrl: string | null;
  checkInStr: string;
  checkOutStr: string;
  lastOccupiedStr: string;
  sameDayIn: boolean;
  sameDayOut: boolean;
};

type TurnoverMarker = {
  key: string;
  checkoutDate: string;
  /** Inclusive span start (checkout day) */
  spanStart: string;
  /** Inclusive span end: day before next check-in, or checkout for same-day/open-ended */
  spanEnd: string;
  departingReservationId: string;
  task: CleaningTaskWithRelations | null;
  sameDay: boolean;
  openEnded: boolean;
  assigned: boolean;
  assigneeLabel: string;
  assigneeAvatarUrl: string | null;
  chip: string;
  outGuestPhotoUrl: string | null;
  inGuestPhotoUrl: string | null;
  outGuestName: string | null;
  inGuestName: string | null;
  warn: boolean;
};

export function ScheduleMonthCalendar({
  currentDate,
  onCurrentDateChange,
  reservations,
  tasks,
  isAssigned,
  getAssigneeLabel,
  getAssigneeAvatarUrl,
  selectedKey,
  onSelectTurnover,
  hasIcalUrls,
  syncing,
  onSync,
  onConnectIcal,
  empty,
}: {
  currentDate: Date;
  onCurrentDateChange: (d: Date) => void;
  reservations: ReservationDateRow[];
  tasks: CleaningTaskWithRelations[];
  isAssigned: (task: CleaningTaskWithRelations) => boolean;
  getAssigneeLabel: (task: CleaningTaskWithRelations) => string;
  getAssigneeAvatarUrl?: (task: CleaningTaskWithRelations) => string | null;
  selectedKey: string | null;
  onSelectTurnover: (sel: ScheduleTurnoverSelection) => void;
  hasIcalUrls: boolean;
  syncing: boolean;
  onSync: () => void;
  onConnectIcal?: () => void;
  empty: boolean;
}) {
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const monthWeeks = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  }, [monthStart, monthEnd]);

  const activeReservations = useMemo(
    () =>
      reservations.filter(
        (r) => r.reservation_type !== "blocked" && !(r.summary || "").toLowerCase().includes("not available"),
      ),
    [reservations],
  );

  const stays: StayBar[] = useMemo(() => {
    const sorted = [...activeReservations].sort((a, b) => a.start_date.localeCompare(b.start_date));
    return sorted.map((r) => {
      const checkInStr = r.start_date.slice(0, 10);
      const checkOutStr = r.end_date.slice(0, 10);
      const lastOccupied =
        checkOutStr > checkInStr ? format(subDays(new Date(`${checkOutStr}T12:00:00`), 1), "yyyy-MM-dd") : checkInStr;
      const sameDayOut = sorted.some(
        (other) => other.id !== r.id && other.start_date.slice(0, 10) === checkOutStr,
      );
      const sameDayIn = sorted.some(
        (other) => other.id !== r.id && other.end_date.slice(0, 10) === checkInStr,
      );
      return {
        reservationId: r.id,
        guestName: r.guest_name ?? null,
        guestPhotoUrl: r.guest_photo_url ?? null,
        checkInStr,
        checkOutStr,
        lastOccupiedStr: lastOccupied,
        sameDayIn,
        sameDayOut,
      };
    });
  }, [activeReservations]);

  const taskByReservationId = useMemo(() => {
    const map = new Map<string, CleaningTaskWithRelations>();
    for (const t of tasks) {
      if (t.reservation_id && !map.has(t.reservation_id)) map.set(t.reservation_id, t);
    }
    return map;
  }, [tasks]);

  const markers: TurnoverMarker[] = useMemo(() => {
    const out: TurnoverMarker[] = [];
    for (const r of activeReservations) {
      const checkoutDate = r.end_date.slice(0, 10);
      const task = taskByReservationId.get(r.id) ?? null;
      const linked =
        task ||
        tasks.find((t) => {
          const end =
            (t as { reservation?: { end_date?: string } | null }).reservation?.end_date ||
            t.scheduled_date;
          return end?.slice(0, 10) === checkoutDate && t.reservation_id === r.id;
        }) ||
        null;
      const resolved = resolveTurnoverWindow({
        checkoutDate,
        reservations: activeReservations,
        departingReservationId: r.id,
        taskStatus: linked?.status,
        isTest: !!(linked as { is_test?: boolean } | null)?.is_test || !!r.is_test,
      });
      if (linked?.status === "completed") continue;
      const assigned = linked ? isAssigned(linked) : false;
      // Multi-day: stretch checkout → day before next check-in. Same-day / open-ended: checkout only.
      let spanEnd = checkoutDate;
      if (!resolved.sameDay && !resolved.openEnded && resolved.nextCheckInDate) {
        const next = resolved.nextCheckInDate.slice(0, 10);
        if (next > checkoutDate) {
          spanEnd = format(subDays(new Date(`${next}T12:00:00`), 1), "yyyy-MM-dd");
        }
      }
      out.push({
        key: linked ? `task:${linked.id}` : `needed:${r.id}`,
        checkoutDate,
        spanStart: checkoutDate,
        spanEnd,
        departingReservationId: r.id,
        task: linked,
        sameDay: resolved.sameDay,
        openEnded: resolved.openEnded,
        assigned,
        assigneeLabel: linked && assigned ? getAssigneeLabel(linked) : "Unassigned",
        assigneeAvatarUrl:
          linked && assigned ? getAssigneeAvatarUrl?.(linked)?.trim() || null : null,
        chip: resolved.chip,
        outGuestPhotoUrl: resolved.outGuestPhotoUrl,
        inGuestPhotoUrl: resolved.inGuestPhotoUrl,
        outGuestName: resolved.outGuestName,
        inGuestName: resolved.inGuestName,
        warn: !assigned || resolved.chip === "Overdue",
      });
    }
    return out;
  }, [activeReservations, taskByReservationId, tasks, isAssigned, getAssigneeLabel, getAssigneeAvatarUrl]);

  const selectedSpanDays = useMemo(() => {
    if (!selectedKey) return new Set<string>();
    const m = markers.find((x) => x.key === selectedKey);
    if (!m) return new Set<string>();
    const set = new Set<string>();
    let cur = m.spanStart;
    while (cur <= m.spanEnd) {
      set.add(cur);
      const [y, mo, d] = cur.split("-").map(Number);
      const next = new Date(y!, mo! - 1, d!);
      next.setDate(next.getDate() + 1);
      cur = format(next, "yyyy-MM-dd");
    }
    return set;
  }, [markers, selectedKey]);

  const weekTurnoverBars = useMemo(() => {
    return monthWeeks.map((weekDays) => {
      const weekStartStr = format(weekDays[0]!, "yyyy-MM-dd");
      const weekEndStr = format(weekDays[6]!, "yyyy-MM-dd");
      return markers
        .map((m) => {
          if (m.spanEnd < weekStartStr || m.spanStart > weekEndStr) return null;
          let startCol = weekDays.findIndex((d) => format(d, "yyyy-MM-dd") >= m.spanStart);
          if (m.spanStart < weekStartStr) startCol = 0;
          let endCol = weekDays.findIndex((d) => format(d, "yyyy-MM-dd") === m.spanEnd);
          if (endCol === -1 && m.spanEnd >= weekEndStr) endCol = 6;
          if (startCol < 0 || endCol < 0 || startCol > endCol) return null;
          const cell = 100 / 7;
          const leftPct = startCol * cell;
          const widthPct = (endCol - startCol + 1) * cell;
          const showLabel =
            m.spanStart >= weekStartStr && m.spanStart <= weekEndStr
              ? true
              : startCol === 0 && m.spanStart < weekStartStr;
          return { marker: m, leftPct, widthPct, showLabel };
        })
        .filter(Boolean) as Array<{
        marker: TurnoverMarker;
        leftPct: number;
        widthPct: number;
        showLabel: boolean;
      }>;
    });
  }, [monthWeeks, markers]);

  const weekBars = useMemo(() => {
    return monthWeeks.map((weekDays) => {
      const weekStartStr = format(weekDays[0]!, "yyyy-MM-dd");
      const weekEndStr = format(weekDays[6]!, "yyyy-MM-dd");
      return stays
        .map((stay) => {
          // Visual span: check-in … last occupied, plus half checkout when same-day out
          const visualEnd = stay.sameDayOut ? stay.checkOutStr : stay.lastOccupiedStr;
          const visualStart = stay.checkInStr;
          if (visualEnd < weekStartStr || visualStart > weekEndStr) return null;

          let startCol = weekDays.findIndex((d) => format(d, "yyyy-MM-dd") >= visualStart);
          if (visualStart < weekStartStr) startCol = 0;
          let endCol = weekDays.findIndex((d) => format(d, "yyyy-MM-dd") === visualEnd);
          if (endCol === -1 && visualEnd >= weekEndStr) endCol = 6;
          if (startCol < 0 || endCol < 0 || startCol > endCol) return null;

          const cell = 100 / 7;
          let leftPct = startCol * cell;
          let widthPct = (endCol - startCol + 1) * cell;
          if (stay.sameDayIn && format(weekDays[startCol]!, "yyyy-MM-dd") === stay.checkInStr) {
            leftPct += cell * 0.5;
            widthPct -= cell * 0.5;
          }
          if (stay.sameDayOut && format(weekDays[endCol]!, "yyyy-MM-dd") === stay.checkOutStr) {
            widthPct -= cell * 0.5;
          }
          if (widthPct <= 0) return null;

          const showLabel =
            stay.checkInStr >= weekStartStr && stay.checkInStr <= weekEndStr
              ? true
              : startCol === 0 && stay.checkInStr < weekStartStr;

          return { stay, leftPct, widthPct, showLabel };
        })
        .filter(Boolean) as Array<{
        stay: StayBar;
        leftPct: number;
        widthPct: number;
        showLabel: boolean;
      }>;
    });
  }, [monthWeeks, stays]);

  const handleMarkerClick = (m: TurnoverMarker) => {
    onSelectTurnover({
      task: m.task,
      departingReservationId: m.departingReservationId,
      checkoutDate: m.checkoutDate,
    });
  };

  return (
    <div className="flex min-w-0 flex-col gap-[18px]" data-tour="tour-schedule-month">
      <div className="flex items-center gap-3">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onCurrentDateChange(subMonths(currentDate, 1))}
            className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-[#e6edf1] bg-white text-[#475569] hover:border-[#24a89a]"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2.3} />
          </button>
          <button
            type="button"
            onClick={() => onCurrentDateChange(addMonths(currentDate, 1))}
            className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-[#e6edf1] bg-white text-[#475569] hover:border-[#24a89a]"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2.3} />
          </button>
        </div>
        <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720] max-sm:text-[16px]">
          {format(currentDate, "MMMM yyyy")}
        </div>
        <button
          type="button"
          onClick={() => onCurrentDateChange(new Date())}
          className="inline-flex h-8 items-center rounded-[10px] border border-[#e6edf1] bg-white px-3.5 text-[13px] font-bold text-[#475569] hover:border-[#24a89a] hover:text-[#15776d] max-sm:h-8 max-sm:px-3 max-sm:text-[12.5px]"
        >
          Today
        </button>
      </div>

      <div className="relative min-w-0 overflow-hidden rounded-[16px] border border-[#e6edf1] bg-white">
        <div className="grid grid-cols-7 border-b border-[#eef3f6]">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center text-[11px] font-bold tracking-[0.4px] text-[#8494a2] max-sm:text-[10px]"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="flex flex-col">
          {monthWeeks.map((week, wi) => (
            <div
              key={wi}
              className="relative grid grid-cols-7 border-b border-[#eef3f6] last:border-b-0"
              style={{ minHeight: 112 }}
            >
              {week.map((day) => {
                const dayStr = format(day, "yyyy-MM-dd");
                const inMonth = isSameMonth(day, currentDate);
                const selectedHere = selectedSpanDays.has(dayStr);
                return (
                  <div
                    key={dayStr}
                    className={cn(
                      "relative min-h-[112px] border-r border-[#eef3f6] last:border-r-0 p-1.5 max-sm:min-h-[96px]",
                      !inMonth && "bg-[#f8fafb]",
                      selectedHere && "bg-[#f2fbfa]",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-7 w-7 items-center justify-center text-[13px] font-bold max-sm:h-6 max-sm:w-6 max-sm:text-[12px]",
                        isToday(day)
                          ? "rounded-full bg-[#24a89a] text-white"
                          : inMonth
                            ? "text-[#0f1720]"
                            : "text-[#b0bcc4]",
                      )}
                    >
                      {format(day, "d")}
                    </div>
                  </div>
                );
              })}

              {/* Dead stay bars — non-interactive */}
              <div className="pointer-events-none absolute inset-x-0 top-9 z-10 h-7 max-sm:top-8 max-sm:h-6">
                {weekBars[wi]?.map(({ stay, leftPct, widthPct, showLabel }) => (
                  <div
                    key={stay.reservationId}
                    className="absolute top-0 flex h-full cursor-default items-center gap-1.5 overflow-hidden rounded-full bg-[#454f5b] px-1 select-none"
                    style={{ left: `calc(${leftPct}% + 3px)`, width: `calc(${widthPct}% - 6px)` }}
                    aria-hidden
                    tabIndex={-1}
                  >
                    {showLabel ? (
                      <>
                        <span className="hidden h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#8b97a3] sm:flex">
                          {stay.guestPhotoUrl ? (
                            <img
                              src={stay.guestPhotoUrl}
                              alt=""
                              className="h-full w-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className="text-[9px] font-bold text-white">{initialsOf(stay.guestName)}</span>
                          )}
                        </span>
                        <span className="truncate text-[12.5px] font-bold text-white max-sm:text-[11px]">
                          {firstName(stay.guestName)}
                        </span>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>

              {/* Clickable turnover bars — multi-day stretches across the gap */}
              <div className="absolute inset-x-0 top-[68px] z-20 h-[30px] max-sm:top-[60px] max-sm:h-11">
                {weekTurnoverBars[wi]?.map(({ marker: m, leftPct, widthPct, showLabel }) => {
                  const selected = m.key === selectedKey;
                  const label = m.sameDay
                    ? m.assigned
                      ? shortAssignee(m.assigneeLabel)
                      : "Same day"
                    : shortAssignee(m.assigneeLabel);
                  const waiting = m.chip === "Waiting on guest";
                  const guestLeft = m.chip === "Guest left";
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => handleMarkerClick(m)}
                      className={cn(
                        "absolute top-0 flex h-full items-center justify-center gap-1 overflow-hidden rounded-full px-2 text-[12.5px] font-bold max-sm:text-[12.5px]",
                        m.sameDay
                          ? waiting
                            ? "bg-[#fff4e5] text-[#8a5a08]"
                            : "bg-[#24a89a] text-white"
                          : m.warn
                            ? "bg-[#fff4e5] text-[#8a5a08]"
                            : "bg-[#dff0ee] text-[#15776d]",
                        selected && "ring-2 ring-[#0f1720] ring-offset-2 ring-offset-white",
                      )}
                      style={{ left: `calc(${leftPct}% + 3px)`, width: `calc(${widthPct}% - 6px)` }}
                    >
                      {showLabel ? (
                        <>
                          {m.sameDay && m.assigned ? (
                            <span
                              className={cn(
                                "flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden rounded-full text-[8px] font-bold",
                                waiting ? "bg-[#f0e0c4] text-[#8a5a08]" : "bg-white/25 text-white",
                              )}
                            >
                              {m.assigneeAvatarUrl ? (
                                <img
                                  src={m.assigneeAvatarUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                initialsOf(m.assigneeLabel)
                              )}
                            </span>
                          ) : m.sameDay ? (
                            <span className="flex shrink-0 items-center">
                              <span
                                className={cn(
                                  "h-[13px] w-[13px] rounded-full border-[1.5px]",
                                  waiting
                                    ? "border-[#fff4e5] bg-white"
                                    : "border-[#24a89a] bg-white",
                                )}
                              />
                              <span
                                className={cn(
                                  "-ml-1 h-[13px] w-[13px] rounded-full border-[1.5px]",
                                  waiting
                                    ? "border-[#fff4e5] bg-[#8a5a08]"
                                    : "border-[#24a89a] bg-[#0f1720]",
                                )}
                              />
                            </span>
                          ) : null}
                          <span className="truncate">{guestLeft && m.sameDay && !m.assigned ? "Guest left" : label}</span>
                        </>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {empty ? (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center p-6">
            <div className="pointer-events-auto flex items-center gap-4 rounded-[16px] border border-[#e6edf1] bg-white px-6 py-5 shadow-[0_10px_30px_rgba(15,23,32,0.08)]">
              <div className="flex flex-col gap-0.5">
                <div className="text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">No turnovers</div>
                <div className="text-[13px] font-medium text-[#8494a2]">
                  {hasIcalUrls ? "Sync to refresh" : "Link a calendar in Settings"}
                </div>
              </div>
              <button
                type="button"
                disabled={syncing}
                onClick={() => (hasIcalUrls ? onSync() : onConnectIcal?.())}
                className="inline-flex h-[38px] items-center gap-2 rounded-[11px] bg-[#24a89a] px-[18px] text-[14px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Sync
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function turnoverSelectionKey(sel: ScheduleTurnoverSelection | null): string | null {
  if (!sel) return null;
  if (sel.task) return `task:${sel.task.id}`;
  return `needed:${sel.departingReservationId}`;
}
